import React, { useState, useEffect, useMemo } from 'react';
import { Trip, StudentProfile } from '../../../types';
import {
  adminBookingService,
  PassengerManifestItem,
} from '../../../services/adminBookingService';
import { isReturnTrip } from '../../../services/tripService';
import {
  Bus,
  Clock,
  X,
  MessageSquare,
  Armchair,
  CheckCircle2,
  Trash2,
  RefreshCw,
  UserPlus,
  Repeat,
  User,
  Search,
  GraduationCap,
  Printer,
} from 'lucide-react';
import { printTripManifest } from '../../../utils/manifestPrinter';

interface AdminTripPassengerModalProps {
  trip: Trip;
  allTrips: Trip[];
  students: StudentProfile[];
  adminEmail?: string;
  onClose: () => void;
  onTripUpdated: () => void;
}

export const AdminTripPassengerModal: React.FC<AdminTripPassengerModalProps> = ({
  trip,
  allTrips,
  students,
  adminEmail,
  onClose,
  onTripUpdated,
}) => {
  const [passengers, setPassengers] = useState<PassengerManifestItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Active Sub-Modals
  // 1. Move Seat Dialog
  const [movingPassenger, setMovingPassenger] = useState<PassengerManifestItem | null>(null);
  const [targetSeatNumber, setTargetSeatNumber] = useState<number | null>(null);

  // 2. Transfer to Another Trip Dialog
  const [transferringPassenger, setTransferringPassenger] = useState<PassengerManifestItem | null>(null);
  const [targetTripId, setTargetTripId] = useState<string>('');
  const [targetTripDate, setTargetTripDate] = useState<string>(trip.date);
  const [targetTripSeat, setTargetTripSeat] = useState<number | null>(null);
  const [transferReason, setTransferReason] = useState<string>('');
  const [targetTripOccupiedSeats, setTargetTripOccupiedSeats] = useState<number[]>([]);
  const [targetTripLoading, setTargetTripLoading] = useState(false);

  // 3. Postpone Dialog
  const [postponingPassenger, setPostponingPassenger] = useState<PassengerManifestItem | null>(null);
  const [postponeNote, setPostponeNote] = useState<string>('');

  // 4. Assign Vacant Seat Dialog
  const [assigningSeatNumber, setAssigningSeatNumber] = useState<number | null>(null);
  const [assignStudentSearch, setAssignStudentSearch] = useState('');
  const [assignedStudent, setAssignedStudent] = useState<StudentProfile | null>(null);

  // 5. Cancel Booking Dialog
  const [cancellingPassenger, setCancellingPassenger] = useState<PassengerManifestItem | null>(null);
  const [cancelReasonText, setCancelReasonText] = useState<string>('إلغاء بناءً على طلب الطالب أو إدارة التشغيل');

  const totalCapacity = trip.totalSeats || trip.capacity || 50;

  // Load Passengers & Seats
  const loadManifest = async () => {
    setIsLoading(true);
    try {
      const res = await adminBookingService.getTripPassengerManifest(trip);
      setPassengers(res.passengers);
    } catch (err) {
      console.warn('Could not load passengers:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadManifest();
  }, [trip.id]);

  // Vacant seats on this trip
  const vacantSeats = useMemo(() => {
    return passengers.filter((p) => !p.isBooked).map((p) => p.seatNumber);
  }, [passengers]);

  // Booked passengers count
  const bookedCount = useMemo(() => {
    return passengers.filter((p) => p.isBooked).length;
  }, [passengers]);

  // Filter possible target trips for transfer
  const eligibleTransferTrips = useMemo(() => {
    return allTrips.filter(
      (t) =>
        t.id !== trip.id &&
        t.universityId === trip.universityId &&
        t.status !== 'CANCELLED' &&
        (!targetTripDate || t.date === targetTripDate)
    );
  }, [allTrips, trip.id, trip.universityId, targetTripDate]);

  // Load target trip occupied seats when targetTripId changes
  useEffect(() => {
    if (!targetTripId) {
      setTargetTripOccupiedSeats([]);
      setTargetTripSeat(null);
      return;
    }

    const selTrip = allTrips.find((t) => t.id === targetTripId);
    if (!selTrip) return;

    setTargetTripLoading(true);
    adminBookingService
      .getTripPassengerManifest(selTrip)
      .then((res) => {
        const occ = res.passengers.filter((p) => p.isBooked).map((p) => p.seatNumber);
        setTargetTripOccupiedSeats(occ);
      })
      .catch((e) => console.warn(e))
      .finally(() => setTargetTripLoading(false));
  }, [targetTripId, allTrips]);

  // Handler: Move seat on same trip
  const handleExecuteMoveSeat = async () => {
    if (!movingPassenger?.booking || !targetSeatNumber) return;
    setActionLoading(true);
    try {
      const res = await adminBookingService.changePassengerSeat({
        bookingId: movingPassenger.booking.id,
        tripId: trip.id,
        oldSeatNumber: movingPassenger.seatNumber,
        newSeatNumber: targetSeatNumber,
        adminEmail,
      });

      setMessage({ text: res.message, type: 'success' });
      setMovingPassenger(null);
      setTargetSeatNumber(null);
      await loadManifest();
      onTripUpdated();
    } catch (err: any) {
      setMessage({ text: err?.message || 'حدث خطأ أثناء نقل المقعد', type: 'error' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Handler: Transfer passenger to another trip
  const handleExecuteTransferTrip = async () => {
    if (!transferringPassenger?.booking || !targetTripId || !targetTripSeat) return;
    const targetTrip = allTrips.find((t) => t.id === targetTripId);
    if (!targetTrip) return;

    setActionLoading(true);
    try {
      const res = await adminBookingService.transferPassengerToTrip({
        bookingId: transferringPassenger.booking.id,
        oldTripId: trip.id,
        oldSeatNumber: transferringPassenger.seatNumber,
        newTrip: targetTrip,
        newSeatNumber: targetTripSeat,
        adminEmail,
        reason: transferReason,
      });

      setMessage({ text: res.message, type: 'success' });
      setTransferringPassenger(null);
      setTargetTripId('');
      setTargetTripSeat(null);
      await loadManifest();
      onTripUpdated();
    } catch (err: any) {
      setMessage({ text: err?.message || 'حدث خطأ أثناء نقل الراكب للرحلة الأخرى', type: 'error' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Handler: Postpone booking
  const handleExecutePostpone = async () => {
    if (!postponingPassenger?.booking) return;
    setActionLoading(true);
    try {
      const res = await adminBookingService.postponeBooking({
        bookingId: postponingPassenger.booking.id,
        tripId: trip.id,
        seatNumber: postponingPassenger.seatNumber,
        adminEmail,
        postponeNotes: postponeNote,
      });

      setMessage({ text: res.message, type: 'success' });
      setPostponingPassenger(null);
      setPostponeNote('');
      await loadManifest();
      onTripUpdated();
    } catch (err: any) {
      setMessage({ text: err?.message || 'حدث خطأ أثناء تأجيل الرحلة', type: 'error' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Handler: Open Cancel Confirmation Dialog
  const handleOpenCancelDialog = (p: PassengerManifestItem) => {
    setCancellingPassenger(p);
    setCancelReasonText('إلغاء بناءً على طلب الطالب أو إدارة التشغيل');
  };

  // Handler: Execute Cancel booking
  const handleConfirmCancel = async () => {
    if (!cancellingPassenger) return;

    setActionLoading(true);
    try {
      const bId = cancellingPassenger.booking?.id || cancellingPassenger.bookingCode || '';
      const res = await adminBookingService.cancelBooking(
        bId,
        trip.id,
        cancellingPassenger.seatNumber,
        adminEmail,
        cancelReasonText || 'إلغاء من داخل منافيست الرحلة'
      );

      setMessage({ text: res.message, type: 'success' });
      setCancellingPassenger(null);
      await loadManifest();
      onTripUpdated();
    } catch (err: any) {
      setMessage({ text: err?.message || 'حدث خطأ أثناء الإلغاء', type: 'error' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Handler: Assign vacant seat directly
  const handleAssignVacantSeat = async () => {
    if (!assigningSeatNumber || !assignedStudent) return;
    setActionLoading(true);
    try {
      const res = await adminBookingService.bookTripForStudent({
        student: assignedStudent,
        trip,
        seatNumber: assigningSeatNumber,
        paymentMethod: 'CASH',
        isPaid: true,
        adminEmail,
        adminNote: 'حجز مباشر من لوحة مقاعد الرحلة',
      });

      setMessage({ text: res.message, type: 'success' });
      setAssigningSeatNumber(null);
      setAssignedStudent(null);
      setAssignStudentSearch('');
      await loadManifest();
      onTripUpdated();
    } catch (err: any) {
      setMessage({ text: err?.message || 'حدث خطأ أثناء الحجز', type: 'error' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Filter students for assigning seat
  const searchedStudentsForAssign = useMemo(() => {
    if (!assignStudentSearch.trim()) return students.slice(0, 5);
    const q = assignStudentSearch.toLowerCase();
    return students
      .filter(
        (s) =>
          s.fullName?.toLowerCase().includes(q) ||
          s.phoneNumber?.includes(q) ||
          s.email?.toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [students, assignStudentSearch]);

  return (
    <div className="admin-modal-overlay" onClick={onClose} dir="rtl">
      <div
        className="admin-modal-card"
        style={{ maxWidth: '1050px', maxHeight: '92vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="admin-modal-header">
          <div className="flex items-center gap-2.5">
            <Bus size={24} className="text-[#E5232A]" />
            <div>
              <h3 className="text-lg font-bold text-slate-900 leading-tight">
                إدارة ركاب ومقاعد الرحلة: {trip.tripCode}
              </h3>
              <span className="text-xs text-slate-500 font-medium">
                {trip.universityName} — {trip.date} ({trip.departureTime})
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-admin-primary text-xs flex items-center gap-1.5"
              style={{ background: '#0F172A', color: '#FFFFFF', borderColor: '#0F172A', fontWeight: 'bold' }}
              onClick={() => printTripManifest(trip, passengers as any, { mode: 'AUTO' })}
              title="طباعة كشف ركاب الرحلة الرسمي والمنافيست الورقي للمقاعد والركاب"
            >
              <Printer size={15} />
              <span>طباعة المنافيست</span>
            </button>
            <button
              type="button"
              className="btn-admin-outline text-xs flex items-center gap-1"
              style={{ fontSize: '11px', padding: '6px 8px' }}
              onClick={() => printTripManifest(trip, passengers as any, { mode: 'ALL_SEATS' })}
              title={`طباعة كشف كامل مقاعد المركبة (الـ ${totalCapacity} مقعداً بالكامل)`}
            >
              <span>كشف الـ {totalCapacity} مقعد</span>
            </button>
            <button type="button" className="admin-modal-close-btn" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="admin-modal-body space-y-4 overflow-y-auto">
          {/* Notification Alert */}
          {message && (
            <div
              className={`p-3 rounded-xl border flex items-center gap-2 ${
                message.type === 'success'
                  ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                  : 'bg-red-50 text-red-800 border-red-200'
              }`}
            >
              <CheckCircle2 size={18} />
              <span className="text-sm">{message.text}</span>
            </div>
          )}

          {/* Trip Meta Card */}
          <div className="p-4 bg-slate-50 border rounded-2xl grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <span className="text-xs text-slate-500 block">خط السير والاتجاه:</span>
              <strong className="text-slate-800 block">{trip.destination}</strong>
              <span
                className={`dir-badge mt-1 ${
                  isReturnTrip(trip.direction) ? 'return' : 'outbound'
                }`}
              >
                {isReturnTrip(trip.direction) ? 'عودة للإسكندرية' : 'ذهاب للجامعة'}
              </span>
            </div>

            <div>
              <span className="text-xs text-slate-500 block">موعد وتاريخ التحرك:</span>
              <strong className="text-slate-800 block font-mono">{trip.date}</strong>
              <span className="text-xs text-slate-600 font-mono">{trip.departureTime}</span>
            </div>

            <div>
              <span className="text-xs text-slate-500 block">حالة الإشغال:</span>
              <strong className="text-emerald-700 font-bold block font-mono">
                {bookedCount} / {totalCapacity} راكب
              </strong>
              <span className="text-xs text-slate-500">
                شاغر: {Math.max(0, totalCapacity - bookedCount)} مقعد
              </span>
            </div>

            <div>
              <span className="text-xs text-slate-500 block">الحافلة والسائق:</span>
              <strong className="text-slate-800 block">
                {trip.vehiclePlate || 'س ق د 1842'}
              </strong>
              <span className="text-xs text-slate-500 font-mono">
                {trip.driverName || 'كابتن الرحلة'}
              </span>
            </div>
          </div>

          {/* Visual Bus Seat Layout */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                <Armchair size={16} className="text-[#E5232A]" />
                <span>
                  خريطة مقاعد الحافلة (اضغط على مقعد شاغر للحجز المباشر، أو مقعد محجوز لإدارته)
                </span>
              </h4>
              <button
                type="button"
                className="btn-admin-outline text-xs py-1 px-2.5 flex items-center gap-1"
                onClick={loadManifest}
                disabled={isLoading}
              >
                <RefreshCw size={12} className={isLoading ? 'animate-spin' : ''} />
                <span>تحديث</span>
              </button>
            </div>

            <div className="bus-seat-layout-card">
              <div className="bus-front-cab">
                <span>كابينة القيادة والمقدمة 🚍</span>
                <span>سعة: {totalCapacity} مقعد</span>
              </div>

              {isLoading ? (
                <div className="p-8 text-center text-slate-400">
                  <RefreshCw size={24} className="animate-spin mx-auto mb-2 text-[#E5232A]" />
                  <span>جاري تحميل بيانات الركاب...</span>
                </div>
              ) : (
                <div
                  className={
                    totalCapacity <= 14 ? 'bus-seat-grid-14' : 'bus-seat-grid-50'
                  }
                >
                  {passengers.map((p) => {
                    const isBooked = p.isBooked;
                    return (
                      <button
                        key={p.seatNumber}
                        type="button"
                        className={`bus-seat-btn ${
                          isBooked ? 'border-red-300 bg-red-50 text-red-900' : 'border-emerald-200 bg-emerald-50/40 text-emerald-900 hover:bg-emerald-100'
                        }`}
                        onClick={() => {
                          if (isBooked) {
                            // Find and scroll to passenger row, or open move seat
                            setMovingPassenger(p);
                          } else {
                            // Assign seat
                            setAssigningSeatNumber(p.seatNumber);
                          }
                        }}
                        title={
                          isBooked
                            ? `المقعد #${p.seatNumber}: ${p.studentName || 'محجوز'} - اضغط لنقل المقعد أو تعديله`
                            : `المقعد #${p.seatNumber} شاغر - اضغط لتسكين طالب`
                        }
                      >
                        <span className="font-bold">#{p.seatNumber}</span>
                        <span className="text-[10px] truncate max-w-[80px]">
                          {isBooked ? p.studentName?.split(' ')[0] || 'محجوز' : 'شاغر +'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Passenger Table Manifest */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <User size={16} className="text-[#E5232A]" />
                <span>قائمة الركاب والمقاعد المحجوزة ({bookedCount})</span>
              </span>
            </h4>

            {bookedCount === 0 ? (
              <div className="p-6 text-center text-slate-400 border rounded-xl bg-slate-50 text-sm">
                لا يوجد ركاب محجوزين في هذه الرحلة حتى الآن.
              </div>
            ) : (
              <div className="overflow-x-auto border border-slate-200 rounded-xl">
                <table className="admin-data-table w-full">
                  <thead>
                    <tr>
                      <th style={{ width: '80px' }}>المقعد</th>
                      <th>الطالب</th>
                      <th>التذكرة والكود</th>
                      <th>وسيلة وسداد</th>
                      <th>إجراءات التعديل والنقل</th>
                    </tr>
                  </thead>
                  <tbody>
                    {passengers
                      .filter((p) => p.isBooked)
                      .map((p) => {
                        const waUrl = adminBookingService.getWhatsAppUrl(
                          p.studentPhone,
                          `مرحباً يا ${p.studentName}، بخصوص رحلتك لـ ${trip.universityName} يوم ${trip.date} الساعة ${trip.departureTime} (المقعد #${p.seatNumber})...`
                        );

                        return (
                          <tr key={p.seatNumber}>
                            <td>
                              <span className="seat-number-tag font-bold">
                                #{p.seatNumber}
                              </span>
                            </td>
                            <td>
                              <div>
                                <strong className="text-slate-900 block text-sm">
                                  {p.studentName || 'طالب مسجل'}
                                </strong>
                                <span className="text-xs text-slate-500 font-mono" dir="ltr">
                                  {p.studentPhone || '—'}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div>
                                <strong className="font-mono text-[#E5232A] text-xs block">
                                  {p.bookingCode || '—'}
                                </strong>
                                <span className="font-mono text-slate-400 text-[11px]">
                                  {p.ticketId || '—'}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div>
                                <span className="font-bold text-slate-800 text-xs font-mono">
                                  {p.price} ج.م
                                </span>
                                <span
                                  className={`status-pill ${
                                    p.paymentStatus === 'PAID' ? 'confirmed' : 'pending'
                                  } text-[10px] mt-0.5`}
                                >
                                  {p.paymentStatus === 'PAID' ? 'مدفوع' : 'معلق'}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {/* Move Seat Button */}
                                <button
                                  type="button"
                                  className="btn-admin-outline text-xs py-1 px-2 flex items-center gap-1"
                                  title="تغيير مقعد الطالب داخل نفس الباص"
                                  onClick={() => setMovingPassenger(p)}
                                  disabled={actionLoading}
                                >
                                  <Armchair size={13} />
                                  <span>نقل مقعد</span>
                                </button>

                                {/* Transfer Trip Button */}
                                <button
                                  type="button"
                                  className="btn-admin-outline text-xs py-1 px-2 flex items-center gap-1 text-blue-700 border-blue-200 hover:bg-blue-50"
                                  title="نقل الراكب إلى رحلة أو يوم آخر"
                                  onClick={() => setTransferringPassenger(p)}
                                  disabled={actionLoading}
                                >
                                  <Repeat size={13} />
                                  <span>نقل رحلة</span>
                                </button>

                                {/* Postpone Button */}
                                <button
                                  type="button"
                                  className="btn-admin-outline text-xs py-1 px-2 flex items-center gap-1 text-amber-700 border-amber-200 hover:bg-amber-50"
                                  title="تأجيل الرحلة مع حفظ الرصيد"
                                  onClick={() => setPostponingPassenger(p)}
                                  disabled={actionLoading}
                                >
                                  <Clock size={13} />
                                  <span>تأجيل</span>
                                </button>

                                {/* Cancel Button */}
                                <button
                                  type="button"
                                  className="btn-admin-danger text-xs py-1 px-2 flex items-center gap-1"
                                  title="إلغاء حجز المقعد"
                                  onClick={() => handleOpenCancelDialog(p)}
                                  disabled={actionLoading}
                                >
                                  <Trash2 size={13} />
                                  <span>إلغاء</span>
                                </button>

                                {/* WhatsApp Button */}
                                {p.studentPhone && (
                                  <a
                                    href={waUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-200"
                                    title="مراسلة الطالب عبر واتساب"
                                  >
                                    <MessageSquare size={14} />
                                  </a>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="admin-modal-footer">
          <button type="button" className="btn-admin-outline" onClick={onClose}>
            إغلاق النافذة
          </button>
        </div>

        {/* SUB-MODAL 1: Move Seat within Same Trip */}
        {movingPassenger && (
          <div
            className="admin-modal-overlay"
            style={{ zIndex: 1100 }}
            onClick={() => setMovingPassenger(null)}
          >
            <div
              className="admin-modal-card"
              style={{ maxWidth: '560px', width: '95%' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="admin-modal-header">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Armchair size={18} className="text-[#E5232A]" />
                  <span>
                    نقل مقعد الطالب: {movingPassenger.studentName || 'طالب OTO'} (المقعد الحالي #{movingPassenger.seatNumber})
                  </span>
                </h3>
                <button
                  type="button"
                  className="admin-modal-close-btn"
                  onClick={() => setMovingPassenger(null)}
                >
                  <X size={18} />
                </button>
              </div>

              <div className="admin-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* 1. Detailed Student & Ticket Profile Card */}
                <div
                  style={{
                    background: '#F8FAFC',
                    borderRadius: '14px',
                    padding: '14px',
                    border: '1px solid #E2E8F0',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '10px',
                      borderBottom: '1px solid #E2E8F0',
                      paddingBottom: '10px',
                      flexWrap: 'wrap',
                      gap: '8px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '12px',
                          background: '#FEE2E2',
                          color: '#E5232A',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 800,
                          fontSize: '16px',
                        }}
                      >
                        {movingPassenger.studentName ? movingPassenger.studentName.charAt(0) : 'ط'}
                      </div>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: '14px', color: '#0F172A' }}>
                          {movingPassenger.studentName || 'طالب غير محدد'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <GraduationCap size={13} className="text-[#E5232A]" />
                          <span>{movingPassenger.studentUniversity || movingPassenger.booking?.universityName || trip.universityName}</span>
                          {movingPassenger.booking?.studentSnapshot?.college && (
                            <span>• {movingPassenger.booking.studentSnapshot.college}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div style={{ textAlign: 'left' }}>
                      <span
                        style={{
                          background: '#E5232A',
                          color: 'white',
                          padding: '5px 12px',
                          borderRadius: '8px',
                          fontWeight: 800,
                          fontSize: '13px',
                          fontFamily: 'monospace',
                          display: 'inline-block',
                          boxShadow: '0 2px 6px rgba(229,35,42,0.25)',
                        }}
                      >
                        المقعد الحالي #{movingPassenger.seatNumber}
                      </span>
                    </div>
                  </div>

                  {/* Grid of Student & Booking Info */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                      gap: '8px',
                      fontSize: '12px',
                    }}
                  >
                    {/* Phone Number */}
                    <div style={{ background: 'white', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                      <div style={{ color: '#64748B', fontSize: '10px', marginBottom: '2px' }}>رقم الهاتف</div>
                      <div style={{ fontWeight: 700, fontFamily: 'monospace', direction: 'ltr', textAlign: 'right' }}>
                        {movingPassenger.studentPhone || movingPassenger.booking?.studentSnapshot?.phoneNumber || '—'}
                      </div>
                    </div>

                    {/* Booking Code */}
                    <div style={{ background: 'white', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                      <div style={{ color: '#64748B', fontSize: '10px', marginBottom: '2px' }}>كود الحجز</div>
                      <div style={{ fontWeight: 700, fontFamily: 'monospace', color: '#E5232A' }}>
                        {movingPassenger.bookingCode || movingPassenger.booking?.bookingCode || '—'}
                      </div>
                    </div>

                    {/* Ticket ID */}
                    <div style={{ background: 'white', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                      <div style={{ color: '#64748B', fontSize: '10px', marginBottom: '2px' }}>رقم التذكرة</div>
                      <div style={{ fontWeight: 700, fontFamily: 'monospace', color: '#0F172A' }}>
                        {movingPassenger.ticketId || movingPassenger.booking?.ticketId || '—'}
                      </div>
                    </div>

                    {/* Ticket Price & Payment Status */}
                    <div style={{ background: 'white', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                      <div style={{ color: '#64748B', fontSize: '10px', marginBottom: '2px' }}>قيمة التذكرة</div>
                      <div style={{ fontWeight: 700, color: '#16A34A' }}>
                        {movingPassenger.price || trip.price} ج.م •{' '}
                        <span style={{ fontSize: '11px', color: movingPassenger.paymentStatus === 'PAID' ? '#16A34A' : '#D97706' }}>
                          {movingPassenger.paymentStatus === 'PAID' ? 'مدفوع' : 'معلق'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Route / Station If Present */}
                  {(movingPassenger.booking?.pickupStationName || movingPassenger.booking?.studentSnapshot?.assignedRoute) && (
                    <div
                      style={{
                        marginTop: '8px',
                        background: 'white',
                        padding: '6px 10px',
                        borderRadius: '8px',
                        border: '1px solid #E2E8F0',
                        fontSize: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: '#475569',
                      }}
                    >
                      <Bus size={13} className="text-[#E5232A]" />
                      <span>
                        المحطة / الخط المعين:{' '}
                        <strong>
                          {movingPassenger.booking?.pickupStationName || movingPassenger.booking?.studentSnapshot?.assignedRoute}
                        </strong>
                      </span>
                    </div>
                  )}
                </div>

                {/* 2. Visual Seat Grid */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <p style={{ fontSize: '12px', fontWeight: 700, color: '#334155', margin: 0 }}>
                      اختر المقعد الشاغر البديل داخل نفس الحافلة لنقل الطالب إليه فوراً:
                    </p>
                    <span style={{ fontSize: '11px', color: '#64748B', fontWeight: 600 }}>
                      {vacantSeats.length} مقاعد شاغرة متوفرة
                    </span>
                  </div>

                  {vacantSeats.length === 0 ? (
                    <div style={{ padding: '14px', background: '#FEF3C7', color: '#92400E', borderRadius: '12px', fontSize: '12px', textAlign: 'center' }}>
                      لا توجد مقاعد شاغرة أخرى في هذه الحافلة لنقل الطالب إليها.
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))',
                        gap: '8px',
                        maxHeight: '230px',
                        overflowY: 'auto',
                        padding: '10px',
                        background: '#F1F5F9',
                        borderRadius: '12px',
                        border: '1px solid #CBD5E1',
                      }}
                    >
                      {vacantSeats.map((sNum) => {
                        const isSelected = targetSeatNumber === sNum;
                        return (
                          <button
                            key={sNum}
                            type="button"
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '8px 4px',
                              borderRadius: '10px',
                              border: isSelected ? '2px solid #E5232A' : '1px solid #CBD5E1',
                              background: isSelected ? '#E5232A' : 'white',
                              color: isSelected ? 'white' : '#1E293B',
                              boxShadow: isSelected ? '0 2px 8px rgba(229, 35, 42, 0.35)' : 'none',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                            }}
                            onClick={() => setTargetSeatNumber(sNum)}
                          >
                            <Armchair size={16} color={isSelected ? 'white' : '#64748B'} />
                            <span style={{ fontSize: '12px', fontWeight: 800, fontFamily: 'monospace', marginTop: '3px' }}>
                              #{sNum}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {targetSeatNumber && (
                  <div
                    style={{
                      background: '#ECFDF5',
                      border: '1px solid #A7F3D0',
                      borderRadius: '10px',
                      padding: '8px 12px',
                      color: '#065F46',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <CheckCircle2 size={16} />
                    <span>
                      سيتم نقل الطالب من المقعد <strong>#{movingPassenger.seatNumber}</strong> إلى المقعد الجديد <strong>#{targetSeatNumber}</strong>.
                    </span>
                  </div>
                )}
              </div>

              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="btn-admin-outline"
                  onClick={() => setMovingPassenger(null)}
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  className="btn-admin-primary flex items-center gap-1.5"
                  disabled={!targetSeatNumber || actionLoading}
                  onClick={handleExecuteMoveSeat}
                >
                  <CheckCircle2 size={16} />
                  <span>
                    {targetSeatNumber ? `تأكيد نقل المقعد إلى #${targetSeatNumber}` : 'اختر المقعد أولاً'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SUB-MODAL 2: Transfer Passenger to Another Trip */}
        {transferringPassenger && (
          <div
            className="admin-modal-overlay"
            style={{ zIndex: 1100 }}
            onClick={() => setTransferringPassenger(null)}
          >
            <div
              className="admin-modal-card"
              style={{ maxWidth: '640px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="admin-modal-header">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Repeat size={18} className="text-[#E5232A]" />
                  <span>
                    نقل الراكب ({transferringPassenger.studentName}) إلى رحلة أخرى
                  </span>
                </h3>
                <button
                  type="button"
                  className="admin-modal-close-btn"
                  onClick={() => setTransferringPassenger(null)}
                >
                  <X size={18} />
                </button>
              </div>

              <div className="admin-modal-body space-y-4">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-900">
                  سيتم تحرير المقعد الحالي (#{transferringPassenger.seatNumber}) في هذه الرحلة تلقائياً،
                  ونقل بيانات التذكرة والحجز إلى الرحلة الجديدة والمقعد المحدد.
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="admin-form-group">
                    <label className="text-xs font-bold">تاريخ الرحلة البديلة:</label>
                    <input
                      type="date"
                      className="admin-search-input w-full"
                      style={{ paddingRight: '1rem' }}
                      value={targetTripDate}
                      onChange={(e) => setTargetTripDate(e.target.value)}
                    />
                  </div>

                  <div className="admin-form-group">
                    <label className="text-xs font-bold">اختر الرحلة البديلة:</label>
                    <select
                      className="admin-filter-select w-full"
                      value={targetTripId}
                      onChange={(e) => setTargetTripId(e.target.value)}
                    >
                      <option value="">-- اختر الرحلة --</option>
                      {eligibleTransferTrips.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.departureTime} ({t.direction === 'RETURN' ? 'عودة' : 'ذهاب'}) — شاغر:{' '}
                          {t.availableSeats}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Target Seat Picker */}
                {targetTripId && (
                  <div>
                    <label className="text-xs font-bold block mb-1.5">
                      اختر المقعد في الرحلة الجديدة:
                    </label>
                    {targetTripLoading ? (
                      <div className="p-4 text-center text-xs text-slate-400">
                        جاري فحص المقاعد...
                      </div>
                    ) : (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(58px, 1fr))',
                          gap: '6px',
                          maxHeight: '190px',
                          overflowY: 'auto',
                          padding: '10px',
                          background: '#F8FAFC',
                          borderRadius: '12px',
                          border: '1px solid #CBD5E1',
                        }}
                      >
                        {Array.from({ length: 50 }).map((_, idx) => {
                          const sNum = idx + 1;
                          const isOccupied = targetTripOccupiedSeats.includes(sNum);
                          const isSelected = targetTripSeat === sNum;
                          return (
                            <button
                              key={sNum}
                              type="button"
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '6px 2px',
                                borderRadius: '8px',
                                border: isSelected
                                  ? '2px solid #E5232A'
                                  : isOccupied
                                  ? '1px solid #E2E8F0'
                                  : '1px solid #CBD5E1',
                                background: isOccupied
                                  ? '#E2E8F0'
                                  : isSelected
                                  ? '#E5232A'
                                  : 'white',
                                color: isOccupied
                                  ? '#94A3B8'
                                  : isSelected
                                  ? 'white'
                                  : '#1E293B',
                                cursor: isOccupied ? 'not-allowed' : 'pointer',
                                transition: 'all 0.15s ease',
                              }}
                              disabled={isOccupied}
                              onClick={() => setTargetTripSeat(sNum)}
                            >
                              <Armchair size={13} color={isOccupied ? '#94A3B8' : isSelected ? 'white' : '#64748B'} />
                              <span style={{ fontSize: '11px', fontWeight: 800, fontFamily: 'monospace', marginTop: '2px' }}>
                                #{sNum}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                <div className="admin-form-group">
                  <label className="text-xs font-bold">سبب النقل (اختياري):</label>
                  <input
                    type="text"
                    className="admin-search-input w-full"
                    style={{ paddingRight: '1rem' }}
                    placeholder="مثال: تغيير موعد المحاضرة / طلب الطالب..."
                    value={transferReason}
                    onChange={(e) => setTransferReason(e.target.value)}
                  />
                </div>
              </div>

              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="btn-admin-outline"
                  onClick={() => setTransferringPassenger(null)}
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  className="btn-admin-primary flex items-center gap-1.5"
                  disabled={!targetTripId || !targetTripSeat || actionLoading}
                  onClick={handleExecuteTransferTrip}
                >
                  <CheckCircle2 size={16} />
                  <span>تأكيد نقل الراكب للرحلة الجديدة</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SUB-MODAL 3: Postpone Booking */}
        {postponingPassenger && (
          <div
            className="admin-modal-overlay"
            style={{ zIndex: 1100 }}
            onClick={() => setPostponingPassenger(null)}
          >
            <div
              className="admin-modal-card"
              style={{ maxWidth: '480px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="admin-modal-header">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Clock size={18} className="text-amber-600" />
                  <span>تأجيل رحلة الطالب: {postponingPassenger.studentName}</span>
                </h3>
                <button
                  type="button"
                  className="admin-modal-close-btn"
                  onClick={() => setPostponingPassenger(null)}
                >
                  <X size={18} />
                </button>
              </div>

              <div className="admin-modal-body space-y-3">
                <p className="text-xs text-slate-600">
                  سيتم تفريغ المقعد رقم #{postponingPassenger.seatNumber} فوراً وإتاحته للركاب الآخرين، مع
                  تحويل حالة الحجز إلى (مؤجل) والاحتفاظ بحق الطالب المالي.
                </p>

                <div className="admin-form-group">
                  <label className="text-xs font-bold">ملاحظات التأجيل:</label>
                  <input
                    type="text"
                    className="admin-search-input w-full"
                    style={{ paddingRight: '1rem' }}
                    placeholder="مثال: تم التأجيل بناءً على اعتذار الطالب هاتفياً..."
                    value={postponeNote}
                    onChange={(e) => setPostponeNote(e.target.value)}
                  />
                </div>
              </div>

              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="btn-admin-outline"
                  onClick={() => setPostponingPassenger(null)}
                >
                  تراجع
                </button>
                <button
                  type="button"
                  className="btn-admin-primary bg-amber-600 hover:bg-amber-700 border-amber-600 flex items-center gap-1.5"
                  disabled={actionLoading}
                  onClick={handleExecutePostpone}
                >
                  <Clock size={16} />
                  <span>تأكيد تأجيل الرحلة وتحرير المقعد</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SUB-MODAL 4: Assign Vacant Seat Directly */}
        {assigningSeatNumber && (
          <div
            className="admin-modal-overlay"
            style={{ zIndex: 1100 }}
            onClick={() => setAssigningSeatNumber(null)}
          >
            <div
              className="admin-modal-card"
              style={{ maxWidth: '520px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="admin-modal-header">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <UserPlus size={18} className="text-emerald-600" />
                  <span>تسكين طالب في المقعد الشاغر #{assigningSeatNumber}</span>
                </h3>
                <button
                  type="button"
                  className="admin-modal-close-btn"
                  onClick={() => setAssigningSeatNumber(null)}
                >
                  <X size={18} />
                </button>
              </div>

              <div className="admin-modal-body space-y-4">
                {assignedStudent ? (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between">
                    <div>
                      <strong className="text-sm text-slate-900 block">{assignedStudent.fullName}</strong>
                      <span className="text-xs text-slate-600 font-mono" dir="ltr">
                        {assignedStudent.phoneNumber}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="text-xs text-red-600 font-bold"
                      onClick={() => setAssignedStudent(null)}
                    >
                      تغيير
                    </button>
                  </div>
                ) : (
                  <div>
                    <label className="text-xs font-bold block mb-1">ابحث عن الطالب المسجل:</label>
                    <div className="admin-search-wrapper w-full">
                      <Search size={16} />
                      <input
                        type="text"
                        className="admin-search-input"
                        placeholder="ابحث بالاسم أو رقم الهاتف..."
                        value={assignStudentSearch}
                        onChange={(e) => setAssignStudentSearch(e.target.value)}
                      />
                    </div>

                    <div className="mt-2 max-h-40 overflow-y-auto border rounded-xl divide-y bg-white">
                      {searchedStudentsForAssign.map((s) => (
                        <div
                          key={s.uid}
                          className="p-2 hover:bg-slate-50 cursor-pointer flex items-center justify-between text-xs"
                          onClick={() => setAssignedStudent(s)}
                        >
                          <strong className="text-slate-800">{s.fullName}</strong>
                          <span className="text-slate-500 font-mono" dir="ltr">
                            {s.phoneNumber}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="btn-admin-outline"
                  onClick={() => setAssigningSeatNumber(null)}
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  className="btn-admin-primary flex items-center gap-1.5"
                  disabled={!assignedStudent || actionLoading}
                  onClick={handleAssignVacantSeat}
                >
                  <CheckCircle2 size={16} />
                  <span>تأكيد حجز المقعد للطالب</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SUB-MODAL 5: Cancel Booking Confirmation */}
        {cancellingPassenger && (
          <div className="admin-modal-overlay" style={{ zIndex: 1100 }}>
            <div className="admin-modal-card" style={{ maxWidth: '520px', borderRadius: '16px' }}>
              <div className="admin-modal-header" style={{ borderBottom: '1px solid #FEE2E2', background: '#FEF2F2' }}>
                <div className="flex items-center gap-2 text-rose-700">
                  <Trash2 size={20} />
                  <h3 className="text-base font-bold text-rose-800">تأكيد إلغاء حجز المقعد وتحريره</h3>
                </div>
                <button
                  type="button"
                  className="admin-modal-close-btn"
                  onClick={() => setCancellingPassenger(null)}
                  disabled={actionLoading}
                >
                  <X size={18} />
                </button>
              </div>

              <div className="admin-modal-body p-5">
                <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-xl mb-4 text-xs text-rose-800 leading-relaxed font-medium">
                  ⚠️ سيتم إلغاء تذكرة الراكب وتحرير المقعد <strong>(#{cancellingPassenger.seatNumber})</strong> فوراً ليصبح متاحاً للطلاب الآخرين في جدول الرحلة.
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs">
                    <span className="text-slate-500">اسم الطالب:</span>
                    <strong className="text-slate-800 font-bold">{cancellingPassenger.studentName || '—'}</strong>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs">
                    <span className="text-slate-500">رقم المقعد الحالي:</span>
                    <strong className="text-rose-700 font-black font-mono text-sm">#{cancellingPassenger.seatNumber}</strong>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs">
                    <span className="text-slate-500">كود الحجز / التذكرة:</span>
                    <strong className="text-slate-700 font-mono">
                      {cancellingPassenger.bookingCode || cancellingPassenger.ticketId || '—'}
                    </strong>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      سبب الإلغاء (للتوثيق وسجل العمليات):
                    </label>
                    <input
                      type="text"
                      className="admin-input-field text-xs w-full p-2.5 border rounded-lg"
                      value={cancelReasonText}
                      onChange={(e) => setCancelReasonText(e.target.value)}
                      placeholder="اكتب سبب إلغاء الحجز..."
                    />
                  </div>
                </div>
              </div>

              <div className="admin-modal-footer flex items-center justify-end gap-2 p-4 border-t bg-slate-50">
                <button
                  type="button"
                  className="btn-admin-outline text-xs px-4 py-2"
                  onClick={() => setCancellingPassenger(null)}
                  disabled={actionLoading}
                >
                  تراجع
                </button>
                <button
                  type="button"
                  className="btn-admin-danger flex items-center gap-1.5 text-xs px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-lg shadow-sm"
                  onClick={handleConfirmCancel}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <RefreshCw size={14} className="animate-spin" />
                  ) : (
                    <Trash2 size={14} />
                  )}
                  <span>تأكيد الإلغاء وتحرير المقعد الآن</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
